import { useMemo, useRef, useSyncExternalStore } from "react";
import { MIX_REGISTERED_SLUGS } from "@/config/browserBoard";
import { findMixLineupEntry } from "@/config/mixLineup";
import { BoardAddition } from "@/features/settings/components/sections/BoardAddition";
import { BoardSelectionCards } from "@/features/settings/components/sections/BoardSelectionCards";
import { useMixCandidateSlugs } from "@/features/settings/hooks/useMixCandidateSlugs";
import { createMixSettingsMediator } from "@/features/settings/mediators/mixSettingsMediator";
import {
  isSettingsEvent,
  type SettingsEvent,
} from "@/features/settings/mediators/settingsEvents";
import {
  defaultMixBoardSetting,
  MIX_SHARE_PERCENTS,
  type MixBoardSettings,
  useMixStore,
} from "@/features/settings/stores/mixStore";
import { Select, SettingRow } from "@/features/settings/ui";
import {
  getAddedBoardNames,
  subscribeAddedBoardNames,
} from "@/shared/lib/addedBoardNames";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
} from "@/shared/ui/component-hierarchy/hooks";

interface MixSettingsProps {
  /**
   * ラインナップ順の登録済み総合カタログ対象slug。
   * 通常はruntime configから取るが、テストでは明示指定する。
   */
  registeredSlugs?: readonly string[];
}

interface MixSettingsViewProps {
  readonly registeredSlugs: readonly string[];
  readonly boardSettings: MixBoardSettings;
  readonly boardNames: Readonly<Record<string, string>>;
  readonly onEvent: (event: SettingsEvent) => void;
}

function boardDisplayName(
  slug: string,
  names: Readonly<Record<string, string>>,
): string {
  const entry = findMixLineupEntry(slug);
  return entry ? `${entry.emoji} ${entry.label}` : (names[slug] ?? slug);
}

function boardPlainName(
  slug: string,
  names: Readonly<Record<string, string>>,
): string {
  return findMixLineupEntry(slug)?.label ?? names[slug] ?? slug;
}

/**
 * 総合カタログの板構成設定。基本設定でカードから混ぜる板を選び、
 * 拡張設定(既定で閉じたアコーディオン)でONの板ごとの取り分を選ぶ。
 */
export const MixSettings: React.FunctionComponent<MixSettingsProps> = ({
  registeredSlugs: runtimeSlugs = MIX_REGISTERED_SLUGS,
}: MixSettingsProps) => {
  const registeredSlugs = useMixCandidateSlugs(runtimeSlugs);
  const boardNames = useSyncExternalStore(
    subscribeAddedBoardNames,
    getAddedBoardNames,
    getAddedBoardNames,
  );
  const boardSettings = useMixStore((state) => state.boardSettings);
  const setBoardShare = useMixStore((state) => state.setBoardShare);
  const boardSettingsRef = useRef(boardSettings);
  boardSettingsRef.current = boardSettings;
  const mediator = useMemo(
    () =>
      createMixSettingsMediator(
        (slug) =>
          (boardSettingsRef.current[slug] ?? defaultMixBoardSetting(slug))
            .share,
        (effect) => setBoardShare(effect.slug, effect.share),
      ),
    [setBoardShare],
  );
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) => {
    if (
      !isSettingsEvent(event) ||
      event.type !== "settings/mix-board-share-changed"
    ) {
      return "unhandled";
    }
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });

  return (
    <ComponentScope node={node}>
      <MixSettingsView
        registeredSlugs={registeredSlugs}
        boardSettings={boardSettings}
        boardNames={boardNames}
        onEvent={(event): void => {
          node.dispatch(event);
        }}
      />
    </ComponentScope>
  );
};

export const MixSettingsView: React.FunctionComponent<MixSettingsViewProps> = ({
  registeredSlugs,
  boardSettings,
  boardNames,
  onEvent,
}: MixSettingsViewProps) => {
  const enabledSlugs = registeredSlugs.filter(
    (slug) => (boardSettings[slug] ?? defaultMixBoardSetting(slug)).enabled,
  );

  return (
    <section aria-label="統合カタログ設定" className="bg-transparent">
      <BoardSelectionCards registeredSlugs={registeredSlugs} compact />
      <BoardAddition defaultSlugs={registeredSlugs} />
      <details className="px-4 py-3">
        <summary className="cursor-pointer text-sm font-bold text-foreground">
          拡張設定
        </summary>
        <section
          className="mt-3 divide-y divide-border"
          aria-label="板ごとの拡張設定"
        >
          <ul
            aria-label="板ごとの表示割合"
            className="m-0 list-none divide-y divide-border p-0"
          >
            {enabledSlugs.map((slug) => {
              const setting =
                boardSettings[slug] ?? defaultMixBoardSetting(slug);
              return (
                <li key={slug}>
                  <SettingRow label={boardDisplayName(slug, boardNames)}>
                    <Select
                      value={
                        setting.share === "uncapped"
                          ? "uncapped"
                          : String(setting.share)
                      }
                      onChange={(value: string): void =>
                        onEvent({
                          type: "settings/mix-board-share-changed",
                          slug,
                          value,
                        })
                      }
                      aria-label={`${boardPlainName(slug, boardNames)}の割合`}
                    >
                      <option value="uncapped">制御なし</option>
                      {[...MIX_SHARE_PERCENTS].reverse().map((pct) => (
                        <option key={pct} value={String(pct)}>
                          全体の{pct}%
                        </option>
                      ))}
                    </Select>
                  </SettingRow>
                </li>
              );
            })}
          </ul>
          <SettingRow description="制御なしの板は全体の並び(bump順など)にそのまま混ざります。%は1ページに混ざる枚数の上限の目安で、板のスレ数やNG設定により前後します。すべての板に%を付けた場合、1ページの枚数は取り分の合計までになります。" />
        </section>
      </details>
    </section>
  );
};
